import { IApp } from '@/types'
import axios, { AxiosError, type AxiosRequestConfig, type Method } from 'axios'
import { toast, type ToastType } from 'vue3-toastify'

const axiosInstance = axios.create()

interface MAxiosRequestConfig extends AxiosRequestConfig {
  method: Method
}

export const sendRequest = async <T>(
  config: MAxiosRequestConfig,
): Promise<IApp.IApiResponse<T>> => {
  try {
    const response = await axiosInstance.request(config)
    console.log('response', response)
    return {
      status: true,
      data: response.data as T,
      headers: response.headers,
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      return {
        status: false,
        title: error.response?.data.error,
        message: error.response?.data.message ?? 'Something went wrong',
        type: error.response?.data.title,
      }
    } else if (error instanceof Error) {
      return {
        status: false,
        title: 'Unknown Error',
        message: error.message ?? 'Something went wrong',
        type: Error.name,
      }
    } else {
      return {
        title: 'Unknown Error',
        status: false,
        message: 'Something went wrong',
        type: 'UnknownError',
      }
    }
  }
}

export const isLoading = (status: IApp.ILoadingStatus) => {
  return status === IApp.ILoadingStatus.Loading
}

export const makeAlert = (message: string, type: ToastType = 'info') => {
  toast(message, {
    autoClose: 3000,
    hideProgressBar: true,
    type: type,
  })
}

export const prettyDate = (iso: string): string => {
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  })
}

const toValidDateOrNull = (value: unknown): Date | null => {
  if (value == null) return null
  const d = new Date(value as any)
  return Number.isNaN(d.getTime()) ? null : d
}

export const formatDateTimeWithTzShort = (
  value: unknown,
  opts?: { timeZone?: string },
): string => {
  const d = toValidDateOrNull(value)
  if (!d) return typeof value === 'string' ? value : String(value ?? '')

  const timeZone = opts?.timeZone
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(d)
}

export const formatTimeWithTzShort = (
  value: unknown,
  opts?: { timeZone?: string },
): string => {
  const d = toValidDateOrNull(value)
  if (!d) return typeof value === 'string' ? value : String(value ?? '')

  const timeZone = opts?.timeZone
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(d)
}

const timeZoneValidityCache = new Map<string, boolean>()

export const isValidTimeZone = (timeZone: string): boolean => {
  const tz = String(timeZone || '').trim()
  if (!tz) return false
  const cached = timeZoneValidityCache.get(tz)
  if (cached !== undefined) return cached
  try {
    // Will throw RangeError if `timeZone` is not recognized in this runtime's ICU data.
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date(0))
    timeZoneValidityCache.set(tz, true)
    return true
  } catch {
    timeZoneValidityCache.set(tz, false)
    return false
  }
}

const formatDateTimeCore = (d: Date, opts?: { timeZone?: string }): string => {
  const timeZone = opts?.timeZone
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  }).format(d)
}

const formatTimeCore = (d: Date, opts?: { timeZone?: string }): string => {
  const timeZone = opts?.timeZone
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  }).format(d)
}

export const dealerTimeTooltipText = (params: {
  value: unknown
  dealerTimeZone?: string
  kind?: 'dateTime' | 'time'
  label?: string
}): string => {
  const label = String(params.label || 'Dealer time').trim() || 'Dealer time'
  const kind = params.kind === 'time' || params.kind === 'dateTime' ? params.kind : 'dateTime'
  const d = toValidDateOrNull(params.value)
  if (!d) return ''

  const tz = String(params.dealerTimeZone || '').trim()
  if (!tz || !isValidTimeZone(tz)) return `${label}: not available`

  const localCore = kind === 'time' ? formatTimeCore(d) : formatDateTimeCore(d)
  const dealerCore = kind === 'time' ? formatTimeCore(d, { timeZone: tz }) : formatDateTimeCore(d, { timeZone: tz })

  if (localCore === dealerCore) return `${label}: same as your time`

  const dealerWithTz =
    kind === 'time' ? formatTimeWithTzShort(d, { timeZone: tz }) : formatDateTimeWithTzShort(d, { timeZone: tz })
  return `${label}: ${dealerWithTz}`
}
